export const privacyPolicyData = {
  PrivacyPolicy: {
    Heading: "ESquadifi Privacy Policy",
    SubHeading: "Effective Date: September 14, 2024",
    Steps: [
      {
        title: "1. Introduction",
        content: [
          `Squadifi LTD ("Squadifi", "we", "our", or "us") respects your privacy and is committed to protecting your personal data. This Privacy Policy explains how we collect, use, share, and protect information in relation to our esports platform and associated services, including our website, applications, and any other services provided (collectively, the "Services"). By using the Services, you consent to the collection and use of your personal information as described in this Privacy Policy.`,
        ],
      },
      {
        title: "2. Data Controller",
        content: [
          "Squadifi LTD, located at 1st Floor, 85 Mercian Way, Edgeley, Stockport, SK3 9DF, England, is the data controller for the processing of your personal information.",
        ],
      },
      {
        title: "3. Information We Collect",
        subSteps: [
          {
            subTitle: "A. Information You Provide Directly",
            subContent: [
              "Account Registration: When you create an account, we collect personal details such as your name, email address, date of birth, and payment details. You may also be required to provide documentation to verify your identity for compliance with laws (e.g., Anti-Money Laundering or AML regulations).",
              "User Content: Content such as gameplay footage, user profiles, and live-streamed events that you upload, create, or share on the platform.",
              "Communication: Any communication between you and Squadifi, such as customer service inquiries or feedback.",
            ],
          },
          {
            subTitle: "B. Automatically Collected Information",
            subContent: [
              "Device Information: Information about your device, such as IP address, device ID, operating system, and browser type.",
              "Usage Data: Information about how you use our Services, including interactions with other users, events attended, wagers placed, and your engagement with in-platform content like ticket purchases or gifts.",
              "Cookies and Tracking Technologies: We use cookies and similar technologies to enhance your experience, track user behaviour, and collect analytic data. For more detailed information, see our Cookies Policy.",
            ],
          },
          {
            subTitle: "C. Information from Third Parties",
            subContent: [
              "Payment Processors: We may receive payment details, such as transaction confirmations from third-party payment providers (e.g., credit card processors or cryptocurrency services).",
              "External Social Networks: If you log in using social media accounts (e.g., Google, Facebook), we may collect certain profile information from those accounts.",
              "Event Organisers and Partners: We may receive details about users from event organisers, including information necessary for payouts, fraud prevention, or user experience improvement.",
            ],
          },
        ],
      },
      {
        title: "4. How We Use Your Information",
        subSteps: [
          {
            subTitle: "A. Service Delivery",
            subContent: [
              "Enable you to participate in esports events, purchase Tokens, wager Belts, and engage with live streams or event content.",
              "Administer user accounts, process transactions, and facilitate payouts for event earnings.",
            ],
          },
          {
            subTitle: "B. Performance and Improvements",
            subContent: [
              "Analyze how you use the platform to develop new features, improve existing ones, and personalise content, such as recommending events or competitions based on your activity.",
              "Improve user engagement through targeted communications, including notifications of upcoming events, special promotions, or updates to the platform.",
            ],
          },
          {
            subTitle: "C. Legal Compliance",
            subContent: [
              "Comply with legal and regulatory obligations, including fraud prevention, anti-money laundering (AML) checks, and tax reporting. We may also retain your data as necessary to fulfil our obligations under consumer protection laws.",
            ],
          },
          {
            subTitle: "D. Security and Fraud Prevention",
            subContent: [
              "Monitor and verify user accounts to detect fraud, unauthorised access, or misuse of the platform. This may involve processing user activity, device information, and IP addresses to protect against abuse or breaches.",
            ],
          },
          {
            subTitle: "E. Marketing and Advertising",
            subContent: [
              "Use your data to send you targeted marketing communications, such as promotions, newsletters, and updates about Squadifi’s Services, only where lawful and subject to your preferences.",
            ],
          },
        ],
      },
      {
        title: "5. How We Share Your Information",
        subSteps: [
          {
            subTitle: "A. Service Providers",
            subContent: [
              "We may share your information with third-party service providers who assist in operating the platform, such as payment processors, cloud hosting providers, data analytics companies, and customer support.",
            ],
          },
          {
            subTitle: "B. Business Partners",
            subContent: [
              "We may share aggregated, anonymised, or non-personally identifiable information with event organisers, advertisers, and partners for marketing, event coordination, or analytics purposes. Squadifi will never sell your personal information to third parties without your explicit consent.",
            ],
          },
          {
            subTitle: "C. Legal Requirements",
            subContent: [
              "We may disclose your information when required to comply with legal obligations, court orders, government authorities, or to protect the rights, safety, or property of Squadifi, our users, or others.",
            ],
          },
          {
            subTitle: "D. Business Transactions",
            subContent: [
              "In the event of a merger, acquisition, or sale of Squadifi, your data may be transferred to the relevant third party involved in the transaction.",
            ],
          },
        ],
      },
      {
        title: "6. Data Security and Retention",
        subSteps: [
          {
            subTitle: "A. Data Security",
            subContent: [
              "We implement appropriate technical and organisational measures to protect your personal data against unauthorised access, alteration, disclosure, or destruction. These include encryption, firewalls, and secure data storage.",
            ],
          },
          {
            subTitle: "B. Data Retention",
            subContent: [
              "We retain personal information for as long as necessary to fulfil the purposes outlined in this Privacy Policy, including to satisfy legal, regulatory, and business requirements. When no longer needed, we will securely delete or anonymise your data.",
            ],
          },
        ],
      },
      {
        title: "7. International Data Transfers",
        content: [
          "As a global platform, Squadifi may transfer your personal data to third parties and servers located outside your home country, including the United States, the European Economic Area (EEA), or other countries. Where applicable, we ensure that international transfers are safeguarded by data protection mechanisms such as standard contractual clauses or adequacy decisions as per GDPR.",
        ],
      },
      {
        title: "8. Your Rights",
        content: [
          "Depending on your jurisdiction, you may have the following rights regarding your personal data:",
        ],
        subContent: [
          "Access: Request a copy of the personal information we hold about you.",
          "Correction: Request correction of inaccurate or incomplete data.",
          "Erasure: Request deletion of your data, subject to certain legal requirements.",
          "Restriction: Request the restriction of processing your personal data.",
          "Data Portability: Request to transfer your personal data to another service provider.",
          "Objection: Object to the processing of your personal data, especially for direct marketing purposes.",
          "Withdrawal of Consent: Where processing is based on consent, you have the right to withdraw consent at any time.",
        ],
      },
      {
        title: "9. Children’s Privacy",
        content: [
          "Squadifi is not intended for use by individuals under the age of 18. If we become aware that we have collected personal information from a minor under 18, we will take steps to delete that information. If you believe we may have inadvertently collected information from a minor, please contact us immediately.",
        ],
      },
      {
        title: "10. Changes to This Policy",
        content: [
          "We may update this Privacy Policy from time to time to reflect changes in our practices or legal requirements. Any changes will be posted on our website, and your continued use of the Services signifies your acceptance of these updates. We encourage you to review this policy regularly.",
        ],
      },
      {
        title: "11. Contact Information",
        content: [
          "If you have any questions or concerns about this Privacy Policy or your personal data, please contact us at:",
          "Squadifi LTD, 1st Floor, 85 Mercian Way, Edgeley, Stockport, SK3 9DF, England",
          "Email: support@esquadifi.io",
        ],
      },
    ],
  },
};
